import { Component, Mixins, Prop } from 'vue-property-decorator'
import { mapActions, mapGetters } from 'vuex'
import { emitter, NumberMixin } from '@kwixeo/interface-vue'
import { KTable } from '@kwixeo/table-vue'
import Table from '@kwixeo/table-vue/dist/components/Table/Table'
import CatalogListMixin from '../../mixins/CatalogListMixin'
import ProductDelete from '../ProductDelete/ProductDelete.vue'
import ProductItem from '../ProductItem/ProductItem.vue'
import { RawLocation } from 'vue-router'

@Component({
  methods: {
    ...mapActions('dynamicModals', ['openModal'])
  },
  computed: {
    ...mapGetters('user', ['can'])
  },
  components: {
    ProductItem,
    ProductDelete,
    KTable
  }
})
export default class ProductList extends Mixins(CatalogListMixin, NumberMixin) {
  openModal!: (param: KwixeoInterface.OpenModalParam) => void
  can!: (key: string, level: number) => boolean

  @Prop({ default: null }) provider!: number|null
  @Prop({ default: 'catalog-products' }) stateKey!: string
  @Prop({ default: false }) disableMainSearchSave!: string|undefined
  @Prop({ default: false }) showGroup!: boolean|undefined

  private productItem: KwixeoCatalog.Product|null = null
  private productToDelete: KwixeoCatalog.Product|null = null

  get include (): string[] {
    const include = ['sale_category', 'vat_rate', 'unit', 'charge_category', 'customFields', 'providers_references']
    if (this.showGroup !== false) {
      include.push('group_contents')
    }
    return include
  }

  get computedStateKey (): string {
    let key = this.stateKey
    if (this.showGroup !== false) {
      key = key.replace('product', 'group')
    }
    return key
  }

  get rowClick (): KwixeoTable.RowClick|null {
    if (this.addAction) {
      return this.addAction
    } else if (this.inlineMode !== false) {
      return null
    } else {
      return (product: KwixeoCatalog.Product) => this.productItem = product
    }
  }

  get rowActions (): KwixeoTable.RowActions {
    const actions: KwixeoTable.RowActions = []

    if (this.can('catalog-products', 3)) {
      actions.push({
        icon: 'edit',
        click: (product: KwixeoCatalog.Product) => {
          this.openModal({
            key: 'catalog-' + (this.showGroup !== false ? 'group' : 'product') + '-form',
            attributes: {
              id: product.id,
              isGroup: this.showGroup !== false
            },
            value: product
          })
        }
      })
    }

    if (this.can('catalog-products', 4)) {
      actions.push({
        icon: 'delete',
        click: (product: KwixeoCatalog.Product) => {
          this.productToDelete = product
        }
      })
    }

    if (this.can('catalog-products', 2)) {
      actions.push({
        icon: ['fas', 'clone'],
        title: this.$t('catalog.' + (this.showGroup !== false ? 'group' : 'product') + '.clone') as string,
        click: (product: KwixeoCatalog.Product) => {
          const clone = JSON.parse(JSON.stringify(product))
          delete clone.id
          this.openModal({
            key: 'catalog-' + (this.showGroup !== false ? 'group' : 'product') + '-form',
            value: clone
          })
        }
      })
    }

    return actions
  }

  get columns (): KwixeoTable.Columns {
    const columns: KwixeoTable.Columns = [
      {
        data: 'reference',
        name: this.$t('catalog.list.reference')
      },
      {
        data: 'name',
        name: this.$t('catalog.list.name')
      },
      {
        data: 'designation',
        name: this.$t('catalog.list.designation'),
        visible: false,
        isHtml: true
      },
      {
        data: 'cant_change_price',
        name: this.$t('catalog.list.cantChangePrice'),
        visible: false
      },
      {
        data: 'unit_price_ht',
        name: this.$t('catalog.list.unitPriceHt'),
        render: 'currency'
      },
      {
        data: 'unit_price_tva',
        name: this.$t('catalog.list.unitPriceTva'),
        render: 'currency',
        visible: false
      },
      {
        data: 'unit_price_ttc',
        name: this.$t('catalog.list.unitPriceTtc'),
        render: 'currency',
        visible: false
      },
      {
        data: 'default_quantity',
        name: this.$t('catalog.list.defaultQuantity'),
        render: 'amount',
        visible: false
      },
      {
        data: 'total_ht',
        name: this.$t('catalog.list.totalHt'),
        render: 'currency',
        visible: false
      },
      {
        data: 'total_tva',
        name: this.$t('catalog.list.totalTva'),
        render: 'currency',
        visible: false
      },
      {
        data: 'total_ttc',
        name: this.$t('catalog.list.totalTtc'),
        render: 'currency',
        visible: false
      }
    ]

    if (this.can('sales-margin', 1) || this.can('catalog-products', 3)) {
      columns.push(...[
        {
          data: 'various_costs',
          name: this.$t('catalog.list.variousCosts'),
          render: 'currency',
          visible: false
        },
        {
          data: 'total_costs',
          name: this.$t('catalog.list.totalCosts'),
          render: 'currency',
          visible: false
        },
        {
          data: 'gross_margin_amount',
          name: this.$t('catalog.list.grossMarginAmount'),
          render: 'currency',
          visible: false
        },
        {
          data: 'gross_margin_rate',
          name: this.$t('catalog.list.grossMarginRate'),
          render: 'pourcent',
          visible: false
        },
        {
          data: 'markup_rate',
          name: this.$t('catalog.list.markupRate'),
          render: 'pourcent',
          visible: false
        }
      ])
    }

    columns.push(...[
      {
        data: 'sale_category.label',
        name: this.$t('catalog.list.saleCategory'),
        visible: false
      },
      {
        data: 'vat_rate.rate',
        name: this.$t('catalog.list.vatRate'),
        render: 'pourcent'
      },
      {
        data: 'unit.label',
        name: this.$t('catalog.list.unit'),
        visible: false
      },
      {
        data: 'status.name',
        name: this.$t('interface.activeStatus.label'),
        visible: false
      }
    ])

    if (!this.showGroup !== false) {
      columns.push(...[
        {
          data: 'not_for_sale',
          name: this.$t('catalog.list.notForSale'),
          visible: false,
          render: (val: boolean) => this.$t('interface.' + (val ? 'yes' : 'no'))
        },
        {
          data: 'charge_category.label',
          name: this.$t('catalog.list.chargeCategory'),
          visible: false
        },
      ])
    }

    columns.push(...[
      {
        data: 'ean_code',
        name: this.$t('catalog.list.ean'),
        visible: false
      },
      {
        data: 'unit_weight',
        name: this.$t('catalog.list.unitWeight'),
        visible: false
      }
    ])

    if (!this.showGroup !== false) {
      columns.push(...[
        {
          data: 'size_length',
          name: this.$t('catalog.list.sizeLength'),
          visible: false
        },
        {
          data: 'size_width',
          name: this.$t('catalog.list.sizeWidth'),
          visible: false
        },
        {
          data: 'size_height',
          name: this.$t('catalog.list.sizeHeight'),
          visible: false
        }
      ])
    }

    columns.push(...[
      {
        data: 'eco_tax_ht',
        name: this.$t('catalog.list.ecoTaxHt'),
        render: 'currency',
        visible: false
      },
      {
        data: 'eco_tax_tva',
        name: this.$t('catalog.list.ecoTaxTva'),
        render: 'currency',
        visible: false
      },
      {
        data: 'eco_tax_ttc',
        name: this.$t('catalog.list.ecoTaxTtc'),
        render: 'currency',
        visible: false
      }
    ])

    if (this.can('sales-margin', 1) || this.can('catalog-products', 3)) {
      columns.push(...[
        {
          data: 'unit_buy_price_ht',
          name: this.$t('catalog.list.unitBuyPriceHt'),
          render: 'currency',
          visible: false
        }
      ])
    }

    if (this.hasModule('stock')) {
      columns.push(...[
        {
          data: 'with_stock',
          name: this.$t('catalog.list.withStock'),
          render: (val: boolean) => this.$t('interface.' + (val ? 'yes' : 'no')),
          visible: false
        },
        {
          data: 'average_buy_price',
          name: this.$t('catalog.list.averageBuyPrice'),
          visible: false,
          render: (val: string|null) => {
            if (val === null) {
              return null
            }
            return this.showNumber(val)
          }
        },
        {
          data: 'stock_quantity',
          name: this.$t('catalog.list.stockQuantity'),
          visible: false,
          render: (val: string|null) => {
            if (val === null) {
              return null
            }
            return this.showNumber(val)
          }
        },
        {
          data: 'total_stock_ht',
          name: this.$t('catalog.list.stockTotal'),
          render: 'currency',
          visible: false
        },
        {
          data: 'stock_theoretical_quantity',
          name: this.$t('catalog.list.stockTheoreticalQuantity'),
          visible: false,
          render: (val: string|null) => {
            if (val === null) {
              return null
            }
            return this.showNumber(val)
          }
        },
        {
          data: 'total_stock_theoretical_ht',
          name: this.$t('catalog.list.stockTheoreticalTotal'),
          render: 'currency',
          visible: false
        },
        {
          data: 'stock_minimum',
          name: this.$t('catalog.list.stockMinimum'),
          visible: false,
          render: (val: number|string|null):  number|string|null => {
            return val === null ? null : this.showNumber(val)
          }
        },
        {
          data: 'stock_maximum',
          name: this.$t('catalog.list.stockMaximum'),
          visible: false,
          render: (val: number|string|null):  number|string|null => {
            return val === null ? null : this.showNumber(val)
          }
        },
        {
          data: 'stock_alert',
          name: this.$t('catalog.list.stockAlert'),
          visible: false,
          render: (val: number|string|null):  number|string|null => {
            return val === null ? null : this.showNumber(val)
          }
        },
        {
          data: 'stock_tracking_mode',
          name: this.$t('catalog.list.stockTrackingMode'),
          visible: false,
          render: (val: number): string => {
            switch (val) {
              case 1:
                return this.$t('catalog.form.traceabilities.lot') as string
              case 2:
                return this.$t('catalog.form.traceabilities.serial') as string
              default:
                return this.$t('catalog.form.traceabilities.no') as string
            }
          }
        }
      ])
    }

    if (!this.showGroup !== false && this.hasModule('catalog-variations')) {
      columns.push(...[
        {
          data: 'variations_length',
          name: this.$t('catalog.list.variationsLength'),
          render: (val: number) => this.$t('interface.' + (val > 0 ? 'yes' : 'no')),
          visible: false
        },
        {
          data: 'variation_level1.label',
          name: this.$t('catalog.list.variationsLevel1'),
          visible: false
        },
        {
          data: 'variation_level2.label',
          name: this.$t('catalog.list.variationsLevel2'),
          visible: false
        },
        {
          data: 'variation_level3.label',
          name: this.$t('catalog.list.variationsLevel3'),
          visible: false
        }
      ])
    }

    if (!this.showGroup !== false) {
      columns.push(...[
        {
          data: 'providers',
          name: this.$t('catalog.list.providers'),
          render: (data: KwixeoCustomers.Customers|null, row: KwixeoCatalog.Product) => {
            if (row.variations_length > 0) {
              return data ? data.map(customer => customer.name).join(', ') : null
            } else {
              return row.providers_references ? row.providers_references.map(reference => reference.customer?.name).join(', ') : null
            }
          },
          visible: false
        },
        {
          data: 'providers_references',
          name: this.$t('catalog.list.providersReferences'),
          render: (data: KwixeoCatalog.ProviderReferences|null) => {
            if (!data) {
              return null
            }
            return data.map(ref => ref.reference).join(', ')
          },
          visible: false
        }
      ])
    }

    columns.push(...[
      {
        data: 'created_at',
        name: this.$t('interface.list.createdAt'),
        visible: false,
        render: 'datetime'
      },
      {
        data: 'updated_at',
        name: this.$t('interface.list.updatedAt'),
        visible: false,
        render: 'datetime'
      },
      ...this.customFieldTableColumns('product')
    ])

    return columns
  }

  get ajaxDatas (): KwixeoTable.Object {
    let filter: KwixeoInterface.GenericObject = {
      is_group: this.showGroup !== false
    }

    if (this.defaultFilters) {
      filter = Object.assign({}, this.defaultFilters, filter)
    }
    if (this.provider) {
      filter.providers = this.provider
    }

    return {
      include: this.computedInclude,
      filter
    }
  }

  get filters (): KwixeoTable.FiltersOption {
    const filters: KwixeoTable.FiltersOption = [
      {
        type: 'text',
        label: this.$t('catalog.filters.reference'),
        name: 'reference'
      },
      {
        type: 'text',
        label: this.$t('catalog.filters.name'),
        name: 'name'
      },
      {
        type: 'select',
        label: this.$t('catalog.filters.saleCategory'),
        name: 'sale_category',
        multiple: true,
        ajax: '/catalog/sale_category?sort=label',
        optionLabel: 'label',
        value: 'id'
      },
      {
        type: 'select',
        label: this.$t('catalog.filters.chargeCategory'),
        name: 'charge_category',
        multiple: true,
        ajax: '/catalog/charge_category?sort=label',
        optionLabel: 'label',
        value: 'id'
      },
      {
        type: 'select',
        label: this.$t('catalog.filters.status'),
        name: 'status',
        options: [
          { value: true, label: this.$t('interface.activeStatus.on') },
          { value: false, label: this.$t('interface.activeStatus.off') }
        ],
        optionLabel: 'label',
        value: 'value'
      },
      {
        type: 'select',
        label: this.$t('catalog.filters.notForSale'),
        name: 'not_for_sale',
        options: [
          { value: true, label: this.$t('interface.yes') },
          { value: false, label: this.$t('interface.no') }
        ],
        optionLabel: 'label',
        value: 'value'
      }
    ]

    if (this.hasModule('stock')) {
      filters.push({
        type: 'select',
        label: this.$t('catalog.filters.withStock'),
        name: 'with_stock',
        options: [
          { value: true, label: this.$t('interface.yes') },
          { value: false, label: this.$t('interface.no') }
        ],
        optionLabel: 'label',
        value: 'value'
      })
    }

    if (this.hasModule('catalog-variations')) {
      filters.push({
        type: 'select',
        label: this.$t('catalog.filters.withVariations'),
        name: 'has_variations',
        options: [
          { value: true, label: this.$t('interface.yes') },
          { value: false, label: this.$t('interface.no') }
        ],
        optionLabel: 'label',
        value: 'value'
      })
    }

    if (!this.provider) {
      filters.push({
        type: 'select',
        label: this.$t('catalog.filters.provider'),
        name: 'providers',
        multiple: true,
        ajax: '/customer?sort=name&filter[is_provider]=1',
        optionLabel: 'name',
        value: 'id'
      })
    }

    filters.push(...[
      {
        type: 'text',
        label: this.$t('catalog.filters.ean'),
        name: 'ean'
      },
      ...this.customFieldTableFilters('product')
    ] as KwixeoTable.FiltersOption)

    return filters.filter(filter => {
      return !this.defaultFilters || this.defaultFilters[filter.name] === undefined
    })
  }

  async mounted (): Promise<void> {
    emitter.on('productCreated', this.productCreated)
    emitter.on('productUpdated', this.productUpdated)
    emitter.on('productDeleted', this.productDeleted)
  }

  destroyed (): void {
    emitter.off('productCreated', this.productCreated)
    emitter.off('productUpdated', this.productUpdated)
    emitter.off('productDeleted', this.productDeleted)
  }

  private rowLink (product: KwixeoCatalog.Product): RawLocation {
    return {
      name: 'catalog-' + (this.showGroup !== false ? 'group' : 'product') + '-item',
      params: {
        id: String(product.id)
      }
    }
  }

  private toDelete (product: KwixeoCatalog.Product) {
    this.productToDelete = product
  }

  private productCreated (product: KwixeoCatalog.Product): void {
    if (product.is_group !== this.showGroup !== false) {
      return
    }

    const table = this.$refs.table as Table
    table.addRow(product)
  }

  private productUpdated (event: KwixeoCatalog.ProductUpdateEvent): void {
    if (event.new.is_group !== this.showGroup !== false) {
      return
    }

    const table = this.$refs.table as Table
    const index = table.getDatas().findIndex(data => data.id === event.new.id)
    if (index >= 0) {
      table.updateRow(index, event.new)
    } else {
      table.addRow(event.new)
    }
  }

  private productDeleted (product: KwixeoCatalog.Product): void {
    const table = this.$refs.table as Table
    const index = table.getDatas().findIndex(data => data.id === product.id)
    if (index >= 0) {
      table.deleteRow(index)
    }
  }
}
